import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  ActivatedRouteSnapshot,
  UrlTree,
} from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  filter,
  map,
  switchMap,
  take,
  timeout,
  catchError,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { CartStore } from '@/cart/cart.store';
import { GlobalStore } from '@/store/app.store';

/**
 * Safety-net timeout (ms) used only for the regionId wait.
 *
 * For the cart itself, we rely on `cartResolved` — a flag the store sets once
 * the first retrieve/create cycle completes — so there is no arbitrary timeout
 * on the cart path. Slow connections simply wait until the response arrives.
 */
const REGION_SAFETY_TIMEOUT_MS = 30_000;

export const CartNotEmptyGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const globalStore = inject(GlobalStore);
  const cartStore = inject(CartStore);
  const router = inject(Router);

  if (route.routeConfig?.path !== 'checkout') {
    return router.createUrlTree(['/productos']);
  }

  const redirectToProducts = () => router.createUrlTree(['/productos']);

  const syncWithGlobal = () => {
    const globChannelId = globalStore.currentSalesChannelId();
    const globalRegionId = globalStore.currentRegionId();
    const currencyCode = globalStore.currentCurrencyCode()?.toUpperCase();

    if (globChannelId && cartStore.salesChannelId() !== globChannelId) {
      cartStore.setSalesChannelId(globChannelId);
    }
    if (globalRegionId && cartStore.regionId() !== globalRegionId) {
      cartStore.setRegionId(globalRegionId);
    }
    if (currencyCode) {
      cartStore.setCurrencyCode(currencyCode);
    }
  };

  const evaluateCart = (): boolean | UrlTree => {
    const cart = cartStore.cart();
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return redirectToProducts();
    }
    return true;
  };

  /**
   * Wait for the store to signal that the initial cart resolution is done.
   *
   * `cartResolved` is set to true in the finalize() of both `retrieveCart`
   * and `createCart`, so it becomes true regardless of success or error —
   * the store has made its determination. No timeout is applied here: slow
   * connections will simply wait until the HTTP response arrives.
   */
  const cartReady$ = toObservable(cartStore.cartResolved).pipe(
    filter(resolved => resolved),
    take(1),
    map(() => evaluateCart())
  );

  // --- Fast synchronous path ---
  // Only short-circuit if the store has already resolved the cart at least once.
  // Avoids the race where cartOperationStatus is 'Idle' because the retrieve
  // hasn't started yet (the effect in withHooks hasn't run, especially on slow
  // connections where regionId arrives late).
  if (cartStore.cartResolved()) {
    const result = evaluateCart();
    if (result === true) {
      syncWithGlobal();
    }
    return result;
  }

  // --- Wait for regionId if not yet available ---
  if (!globalStore.currentRegionId()) {
    return toObservable(globalStore.currentRegionId).pipe(
      filter(regionId => !!regionId),
      take(1),
      timeout(REGION_SAFETY_TIMEOUT_MS),
      catchError(() => of(redirectToProducts())),
      switchMap(res => {
        if (res instanceof UrlTree) return of(res);
        syncWithGlobal();
        return cartReady$;
      })
    );
  }

  syncWithGlobal();
  return cartReady$;
};
